'use client'

import { ColumnDef } from '@tanstack/react-table'
import { Ellipsis, Languages } from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge'
import { TranslationListItem } from '@/lib/api/translations'
import { DateDisplay } from '@/components/date-display'

interface GetTranslationsTableColumnsProps {
	/** Reserved: kept for parity with other store-scoped column builders. */
	storeId?: string
	onEditTranslation: (translationId: string) => void
	onDeleteTranslation: (translationId: string) => void
}

export function useTranslationsTableColumns({
	onEditTranslation,
	onDeleteTranslation,
}: GetTranslationsTableColumnsProps): ColumnDef<TranslationListItem>[] {
	const { t } = useTranslation('languages')

	return React.useMemo(
		() => [
			// 1. Select checkbox
			{
				id: 'select',
				header: ({ table }) => (
					<Checkbox
						checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
						onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
						aria-label={t('table.selectAll')}
						className="translate-y-0.5"
					/>
				),
				cell: ({ row }) => (
					<DataTableCheckbox row={row} />
				),
				enableSorting: false,
				enableHiding: false,
				size: 40,
			},
			// 2. Locale (primary column, clickable to edit)
			{
				id: 'locale',
				accessorKey: 'locale',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.locale')} />,
				cell: ({ row }) => {
					const translation = row.original
					return (
						<div className="min-w-0">
							<button
								type="button"
								onClick={() => onEditTranslation(translation.id)}
								className="truncate font-medium text-left hover:underline"
							>
								{translation.locale}
							</button>
						</div>
					)
				},
				meta: {
					label: t('table.columns.locale'),
					placeholder: t('table.searchPlaceholder'),
					variant: 'text',
					icon: () => <Languages />,
				},
				enableColumnFilter: true,
				enableSorting: true,
			},
			// 3. Country Code
			{
				id: 'countryCode',
				accessorKey: 'countryCode',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.countryCode')} />,
				cell: ({ row }) => {
					const translation = row.original
					return (
						<Badge variant="outline" className="font-mono">
							{translation.countryCode}
						</Badge>
					)
				},
				meta: { label: t('table.columns.countryCode') },
				enableSorting: true,
			},
			// 4. Enabled / Status
			{
				id: 'enabled',
				accessorKey: 'enabled',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.enabled')} />,
				cell: ({ row }) => {
					const translation = row.original
					return (
						<Badge
							variant={translation.enabled ? 'green' : 'gray'}
							className="min-w-[72px] justify-center capitalize"
						>
							{translation.enabled ? t('table.enabled') : t('table.disabled')}
						</Badge>
					)
				},
				meta: { label: t('table.columns.enabled') },
				enableSorting: true,
			},
			// 5. Created At
			{
				id: 'createdAt',
				accessorKey: 'createdAt',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.createdAt')} />,
				cell: ({ row }) => (
					<DateDisplay
						value={row.original.createdAt}
						format="MM/dd/yyyy"
						className="text-sm tabular-nums"
						fallback="—"
					/>
				),
				meta: { label: t('table.columns.createdAt') },
				enableSorting: true,
			},
			// 6. Translation ID (moved to end, copyable) — matches convention in
			// products, orders, customers, payments, email templates, funnels tables.
			{
				id: 'id',
				accessorKey: 'id',
				header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.id')} />,
				cell: ({ row }) => {
					const translation = row.original
					const displayValue = `${translation.id.substring(0, 8)}...`
					return (
						<CopyableResourceBadge
							name={t('table.columns.id')}
							value={displayValue}
							copyValue={translation.id}
						/>
					)
				},
				meta: { label: t('table.columns.id') },
				enableSorting: true,
				enableHiding: true,
				size: 140,
			},
			// 6. Actions
			{
				id: 'actions',
				cell: ({ row }) => {
					const translation = row.original
					return (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									aria-label={t('table.openMenu')}
									variant="ghost"
									className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
								>
									<Ellipsis className="h-4 w-4" aria-hidden="true" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-[160px]">
								<DropdownMenuItem onClick={() => onEditTranslation(translation.id)}>
									{t('table.editAction')}
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									onClick={() => onDeleteTranslation(translation.id)}
									className="text-destructive focus:text-destructive"
								>
									{t('table.deleteAction')}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)
				},
				enableSorting: false,
				enableHiding: false,
				size: 40,
			},
		],
		[t, onEditTranslation, onDeleteTranslation]
	)
}

